import { Map } from "@mui/icons-material";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_GET_MODELS_DATA } from "../../config/api";
import dayjs from "dayjs";
import { checkIrrigationStatus } from "../../utils/irrigationHandlers";
import FieldsListSkeleton from "shared-components/src/components/skeletons/fieldsListSkeleton.component";
import {
  getChipColorByCondition,
  getPriorityByCondition,
} from "../../constants/irrigationConditions";

const FieldsListContainer = ({
  fields,
  t,
  token,
  onIrrigationStatusChange,
}) => {
  const [fieldIrrigationDates, setFieldIrrigationDates] = useState({});
  const [irrigationStatus, setIrrigationStatus] = useState({});
  const [irrigationConditions, setIrrigationConditions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const abortControllerRef = useRef();

  useEffect(() => {
    if (!isLoading && onIrrigationStatusChange) {
      onIrrigationStatusChange(irrigationStatus, isLoading);
    }
  }, [irrigationStatus, onIrrigationStatusChange, isLoading]);

  const fetchModelOutputs = async (id_thing) => {
    try {
      const headers = {
        "content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await axios.get(
        `${API_GET_MODELS_DATA}?thing=${id_thing}`,
        { headers },
      );
      const verticalLines = response.data.verticalLines;

      const conditionObj = verticalLines?.find((line) =>
        line.hasOwnProperty("irrigationCondition"),
      );
      const irrigationCondition = conditionObj.irrigationCondition;

      const earlyDateObj = verticalLines?.find((line) =>
        line.hasOwnProperty("earlyIrrigationDate"),
      );
      const earlyDate = earlyDateObj.earlyIrrigationDate;

      const status = checkIrrigationStatus(verticalLines);

      return {
        id_thing,
        irrigationCondition,
        earlyDate: earlyDate !== "NA" ? earlyDate : null,
        status,
      };
    } catch (error) {
      console.error(
        `Errore nel recupero dei dati per id_thing ${id_thing}:`,
        error,
      );
    }
  };

  useEffect(() => {
    if (!fields || fields.length === 0 || !token) {
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchAllData = async () => {
      setIsLoading(true);

      const fieldsWithThing = fields.filter((field) => field.id_thing);

      const results = await Promise.allSettled(
        fieldsWithThing.map((field) =>
          fetchModelOutputs(field.id_thing, signal),
        ),
      );

      const newDates = {};
      const newStatus = {};
      const newConditions = {};

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          const { id_thing, irrigationCondition, earlyDate, status } =
            result.value;

          newConditions[id_thing] = irrigationCondition;

          if (earlyDate) {
            newDates[id_thing] = earlyDate;
          }

          newStatus[id_thing] = status;
        }
      });

      setFieldIrrigationDates(newDates);
      setIrrigationStatus(newStatus);
      setIrrigationConditions(newConditions);

      setIsLoading(false);
    };

    fetchAllData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fields, token]);

  useEffect(() => {
    if (!isLoading && onIrrigationStatusChange) {
      onIrrigationStatusChange(irrigationStatus, isLoading);
    }
  }, [irrigationStatus, onIrrigationStatusChange, isLoading]);

  const filteredAndSortedFields = fields
    ?.filter((field) => {
      const condition = irrigationConditions[field.id_thing];
      return condition && condition !== "NA";
    })
    .sort((a, b) => {
      const conditionA = irrigationConditions[a.id_thing];
      const conditionB = irrigationConditions[b.id_thing];

      const priorityA = getPriorityByCondition(conditionA);
      const priorityB = getPriorityByCondition(conditionB);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const dateA = fieldIrrigationDates[a.id_thing]
        ? dayjs(fieldIrrigationDates[a.id_thing])
        : dayjs().add(100, "year");
      const dateB = fieldIrrigationDates[b.id_thing]
        ? dayjs(fieldIrrigationDates[b.id_thing])
        : dayjs().add(100, "year");

      return dateA.diff(dateB);
    });

  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        paddingX: "20px",
        paddingBottom: "20px",
        width: "100%",
      }}
    >
      <h3>{t("home.next_fields_to_irrigate")}</h3>
      <Box
        width="100%"
        height="400px"
        display="flex"
        flexDirection="column"
        alignItems="start"
        gap={2}
        overflow="auto"
      >
        {isLoading ? (
          <FieldsListSkeleton />
        ) : filteredAndSortedFields?.length === 0 ? (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography color="text.secondary">
              {t("home.no_irrigation_fields") || "Nessun campo da irrigare"}
            </Typography>
          </Box>
        ) : (
          filteredAndSortedFields?.map((f) => (
            <Box
              border="0.5px solid lightgrey"
              borderRadius="10px"
              p={2}
              width="100%"
              key={f.id}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignContent="center"
            >
              <Stack spacing={2} direction="row">
                <Map />
                <Typography key={f.id}>{f.label}</Typography>
              </Stack>
              {fieldIrrigationDates[f.id_thing] && (
                <Chip
                  label={`${dayjs
                    .utc(fieldIrrigationDates[f.id_thing])
                    .format("DD/MM/YY")}`}
                  size="small"
                  color={getChipColorByCondition(
                    irrigationConditions[f.id_thing],
                  )}
                  variant="outlined"
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default FieldsListContainer;
